import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { ToastTypeEnum } from '../components/organisms/toast/toast.type';
import { ApiRoutes } from '../constants/api-routes.constants';
import { UserGenderEnum, UserRoleEnum } from '../enums/user.enums';
import { User, UserResponseDTO, UsersResponseDTO } from '../types/user.types';
import { createEndpointUrl } from '../utilities/pure-functions';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly http = inject(HttpClient);
  private readonly toastService = inject(ToastService);

  me(): Observable<UserResponseDTO> {
    const url = createEndpointUrl(ApiRoutes.getUserMe);
    return this.http.get<UserResponseDTO>(url, { headers: { addAuthHeader: 'true' } });
  }

  deleteUser(): Observable<void> {
    const url = createEndpointUrl(ApiRoutes.deleteUser);
    return this.http.delete<void>(url, { headers: { addAuthHeader: 'true' } }).pipe(
      tap(() => {
        this.toastService.addToast('Nutzer wurde gelöscht', ToastTypeEnum.SUCCESS);
      }),
      catchError((error) => {
        this.toastService.addToast('Löschen fehlgeschlagen', ToastTypeEnum.ERROR);
        return throwError(() => error);
      })
    );
  }

  updateUser(
    firstname: string,
    lastname: string,
    gender: UserGenderEnum
  ): Observable<UserResponseDTO> {
    const url = createEndpointUrl(ApiRoutes.updateUser);
    return this.http
      .put<UserResponseDTO>(
        url,
        { lastname, firstname, gender },
        { headers: { addAuthHeader: 'true' } }
      )
      .pipe(
        tap(() => {
          this.toastService.addToast('Änderungen gespeichert');
        }),
        catchError((error) => {
          this.toastService.addToast('Fehler beim Speichern', ToastTypeEnum.ERROR);
          return throwError(() => error);
        })
      );
  }

  updatePassword(oldPassword: string, newPassword: string): Observable<void> {
    const url = createEndpointUrl(ApiRoutes.changePassword);

    return this.http
      .patch<void>(url, { oldPassword, newPassword }, { headers: { addAuthHeader: 'true' } })
      .pipe(
        tap(() => {
          this.toastService.addToast('Neues Passwort gespeichert');
        }),
        catchError((error) => {
          this.toastService.addToast('Fehler beim Speichern', ToastTypeEnum.ERROR);
          return throwError(() => error);
        })
      );
  }

  register(
    email: string,
    password: string,
    firstname: string,
    lastname: string,
    gender: UserGenderEnum,
    version: string
  ): Observable<void> {
    const url = createEndpointUrl(ApiRoutes.registerUser);
    return this.http.post<void>(url, { email, password, lastname, firstname, gender, version });
  }

  setPassword(password: string, token: string): Observable<void> {
    const url = createEndpointUrl(ApiRoutes.setPassword);
    return this.http.post<void>(url, { password, token }).pipe(
      tap(() => {
        this.toastService.addToast('Passwort gespeichert');
      }),
      catchError((error) => {
        this.toastService.addToast('Fehler beim Speichern', ToastTypeEnum.ERROR);
        return throwError(() => error);
      })
    );
  }

  resetPassword(email: string): Observable<void> {
    const url = createEndpointUrl(ApiRoutes.resetPw);
    return this.http.post<void>(url, { email });
  }

  getRole(userId: string): Observable<{ role: UserRoleEnum }> {
    const url = createEndpointUrl(ApiRoutes.getUserRole, { id: userId });
    return this.http.get<{ role: UserRoleEnum }>(url, { headers: { addAuthHeader: 'true' } });
  }

  getUserById(userId: string): Observable<User> {
    const url = createEndpointUrl(ApiRoutes.getUserById, { id: userId });
    return this.http.get<User>(url, { headers: { addAuthHeader: 'true' } });
  }

  search(
    pageIndex: number,
    pageSize: number,
    email: string | null,
    firstname: string | null,
    lastname: string | null,
    gender: UserGenderEnum | null,
    activityId?: string
  ): Observable<UsersResponseDTO> {
    const url = createEndpointUrl(ApiRoutes.searchForUser);

    const params = new HttpParams({
      fromObject: {
        ...(email && { email }),
        ...(firstname && { firstname }),
        ...(lastname && { lastname }),
        ...(gender && { gender }),
        ...(pageIndex && { pageIndex }),
        ...(pageSize && { pageSize }),
        ...(activityId && { activityId }),
      },
    });

    return this.http
      .get<UsersResponseDTO>(url, { params, headers: { addAuthHeader: 'true' } })
      .pipe(
        catchError((error) => {
          this.toastService.addToast('Teilnehmersuche fehlgeschlagen', ToastTypeEnum.ERROR);
          return throwError(() => error);
        })
      );
  }
}
